/**
 * @file SVG图标组件
 * @author zhangfengzhi
 * @date 2026-01-01
 * @description 支持预定义图标和外部SVG文件加载的通用SVG图标组件
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

// 全局SVG简单缓存（仅保留内存缓存，移除过期和数量限制）
const svgCache: Record<string, string> = {};

interface SvgIconProps {
    size?: number;
    color?: string;
    className?: string;
    name?: string; // 预定义图标名称
    svgUrl?: string; // SVG文件URL
    style?: React.CSSProperties;
}

// 预定义的SVG图标库（仅保留核心数据）
const iconPaths: Record<string, { path: string; viewBox: string }> = {
    'set-attributes': {
        path: 'M988.156407 93.652838H875.702997A129.395893 129.395893 0 0 0 750.309999 0.003724 129.954437 129.954437 0 0 0 624.637729 93.652838H35.83987a35.467507 35.467507 0 0 0-35.374417 34.25733c0 18.897386 15.825397 34.25733 34.815873 34.25733h589.263312c16.38394 55.761252 67.956117 94.021476 125.765361 93.556023a129.954437 129.954437 0 0 0 125.858451-93.556023h112.5465a34.722783 34.722783 0 0 0 34.722783-34.25733 34.815873 34.815873 0 0 0-35.281326-34.25733zM750.309999 187.11577a59.298693 59.298693 0 1 1 0-118.690477c33.326424 0 60.41578 26.623903 60.41578 59.391784a59.950327 59.950327 0 0 1-60.41578 59.298693z m237.846408 264.377221c18.990476 0 34.815873 14.894491 35.374417 34.35042 0 18.897386-15.825397 34.25733-34.815874 34.25733H399.358548A129.954437 129.954437 0 0 1 273.686278 613.749855a129.954437 129.954437 0 0 1-125.858452-93.556023H35.281326A35.467507 35.467507 0 0 1 0 485.936502c0-18.897386 15.825397-34.25733 34.815873-34.25733h112.45341A131.536976 131.536976 0 0 1 273.686278 358.030058c59.764146 0 110.405417 39.935855 125.76536 93.649114h588.704769zM273.686278 545.328286a59.950327 59.950327 0 0 0 60.322689-59.298693 59.950327 59.950327 0 0 0-60.322689-59.391784 59.950327 59.950327 0 0 0-60.415781 59.391784c0 32.67479 27.089356 59.298693 60.415781 59.298693z m714.935582 315.577034c18.990476 0 34.908964 15.359944 35.374416 34.25733 0 18.990476-15.825397 34.350421-34.815873 34.350421H876.726994a129.954437 129.954437 0 0 1-125.858452 93.556023 129.954437 129.954437 0 0 1-125.858451-93.556023H35.746779a35.467507 35.467507 0 0 1-35.281326-34.350421c0-18.897386 15.825397-34.25733 34.815873-34.25733h589.263312A129.954437 129.954437 0 0 1 750.309999 767.256206c59.857237 0 110.498507 39.842764 125.858451 93.556024h112.5465z m-238.311861 93.649114a59.950327 59.950327 0 0 0 60.41578-59.391784 59.950327 59.950327 0 0 0-60.41578-59.298693 59.950327 59.950327 0 0 0-60.32269 59.298693c0 32.767881 27.089356 59.391784 60.32269 59.391784z',
        viewBox: '0 0 1024 1024',
    },
    'disable-svg': {
        path: 'M512 953.6a441.6 441.6 0 1 1 0-883.2 441.6 441.6 0 0 1 0 883.2z m0-64a377.6 377.6 0 1 0 0-755.2 377.6 377.6 0 0 0 0 755.2z M182.1696 227.4304l45.2608-45.2608 614.4 614.4-45.2608 45.2608z',
        viewBox: '0 0 1024 1024',
    },
    'enable-svg': {
        path: 'M310.122667 133.610667a42.666667 42.666667 0 0 1-11.669334 59.2 386.090667 386.090667 0 0 0-57.984 47.658666A382.592 382.592 0 0 0 128 512c0 212.074667 171.925333 384 384 384s384-171.925333 384-384c0-103.253333-40.896-199.957333-112.469333-271.530667a386.090667 386.090667 0 0 0-57.984-47.658666 42.666667 42.666667 0 1 1 47.530666-70.890667c25.429333 17.066667 49.130667 36.565333 70.784 58.218667A467.904 467.904 0 0 1 981.333333 512c0 259.2-210.133333 469.333333-469.333333 469.333333S42.666667 771.2 42.666667 512A467.904 467.904 0 0 1 180.138667 180.138667a471.424 471.424 0 0 1 70.784-58.218667 42.666667 42.666667 0 0 1 59.2 11.690667zM512 64a42.666667 42.666667 0 0 1 42.56 39.466667L554.666667 106.666667v426.666666a42.666667 42.666667 0 0 1-85.226667 3.2L469.333333 533.333333V106.666667a42.666667 42.666667 0 0 1 42.666667-42.666667z',
        viewBox: '0 0 1024 1024',
    },
    'desen-rules': {
        path: 'M847.616 64c16.896 0 30.9248 12.4416 33.28 28.7232l0.4096 5.0176V477.184a8.448 8.448 0 0 1-5.7856 8.0384l-2.6624 0.4096h-58.9312a8.448 8.448 0 0 1-7.9872-5.7856l-0.4608-2.6624V139.9296H215.808v742.144h172.6976c3.6864 0 6.8608 2.4064 7.9872 5.7856l0.4608 2.6624v59.0336a8.448 8.448 0 0 1-5.7856 7.9872l-2.6624 0.4096H173.6704a33.6896 33.6896 0 0 1-33.28-28.672l-0.4096-5.0176V97.7408c0-16.9472 12.4416-30.976 28.7232-33.3824l5.0176-0.3584h673.8944z m-239.7184 601.8048v68.8128l65.792-20.7872 17.3056 53.6576-65.3312 20.736 40.2432 56.2688-45.4656 33.28-41.1136-55.808-40.2432 55.808-45.4144-33.28 40.2432-56.2688-64.9216-20.736 16.896-53.6576 65.3312 20.736v-68.7616h56.6784z m264.0384 0v68.8128l65.7408-20.7872 17.3056 53.6576-65.3312 20.736 40.2432 56.2688-45.4144 33.28-41.1136-55.808-40.2432 55.808-45.4656-33.28 40.2432-56.2688-64.9216-20.736 16.896-53.6576 65.3312 20.736v-68.7616h56.7296z m-184.8832-251.8016c10.8544 0 20.0192 2.9184 22.1184 6.7584l0.512 1.6896v50.5856c0 4.096-7.8336 7.4752-18.0736 8.2432l-4.5568 0.2048H322.6624c-10.9056 0-20.0192-2.9184-22.1696-6.7584L300.032 473.088v-50.5856c0-4.096 7.8336-7.4752 18.0736-8.2944l4.5568-0.1536h364.3904z m25.7536-151.808c3.7376 0 6.912 2.4064 7.9872 5.7856l0.4608 2.6624v50.5856a8.448 8.448 0 0 1-5.7856 7.9872l-2.6624 0.4608H308.48a8.448 8.448 0 0 1-7.9872-5.7856l-0.4608-2.6624v-50.5856c0-3.7376 2.4576-6.912 5.7856-8.0384l2.6624-0.4096h404.3264z',
        viewBox: '0 0 1024 1024',
    },
    'set-permission': {
        path: 'M938.097304 585.045821a156.614937 156.614937 0 0 1 0 221.240643 156.785604 156.785604 0 0 1-176.753581 31.118188l-2.559997 2.90133-39.25329 39.196401 36.35196 36.351959a44.031951 44.031951 0 0 1-62.236376 62.179487l-36.351959-36.35196-69.461256 69.404368a44.031951 44.031951 0 0 1-62.236376-62.179487l169.699367-169.5287a156.728715 156.728715 0 0 1 21.617754-194.332229 156.614937 156.614937 0 0 1 221.183754 0zM479.459592 0.001138c77.482581 0 150.357166 30.207966 205.027327 84.991905a288.028124 288.028124 0 0 1 84.991906 205.027328A287.800569 287.800569 0 0 1 684.486919 494.933921a287.345459 287.345459 0 0 1-194.389117 84.76435 45.852393 45.852393 0 0 1-10.808877 1.308443 391.964009 391.964009 0 0 0-391.565787 391.622676A43.918173 43.918173 0 0 1 0.000569 972.572502c0-64.682595 12.629319-127.601636 37.603514-186.595349A479.800356 479.800356 0 0 1 292.750466 530.773881l16.042649-6.371548a292.57923 292.57923 0 0 1-34.303962-29.468412A288.028124 288.028124 0 0 1 189.440358 290.020371c0-77.482581 30.151078-150.357166 84.991906-205.027328A288.597013 288.597013 0 0 1 479.459592 0.001138z m348.102724 627.085525a68.551035 68.551035 0 1 0 0 137.045181 68.551035 68.551035 0 0 0 0-137.10207zM479.459592 87.894373a202.296664 202.296664 0 0 0-202.125998 202.125998A202.296664 202.296664 0 0 0 479.459592 492.08948a202.296664 202.296664 0 0 0 202.125997-202.069109 202.296664 202.296664 0 0 0-202.125997-202.125998z',
        viewBox: '0 0 1024 1024',
    },
    'apply-svg': {
        path: 'M828.672 988.16H146.176a119.0912 119.0912 0 0 1-119.296-119.296V186.368C26.368 126.464 81.92 75.264 146.176 75.264h384a33.9456 33.9456 0 0 1 34.304 34.304c0 21.504-16.896 25.6-34.304 25.6h-384a52.0192 52.0192 0 0 0-51.2 51.2v682.496a52.0192 52.0192 0 0 0 51.2 51.2h682.496a52.0192 52.0192 0 0 0 51.2-51.2v-384a33.9456 33.9456 0 0 1 34.304-34.304c21.504 0 34.304 12.8 34.304 34.304v384a119.8592 119.8592 0 0 1-119.808 119.296z M192.768 517.12l6.144-6.144a29.952 29.952 0 0 1 41.984 12.288l102.4 186.88 6.144 6.144a15.36 15.36 0 0 0 9.216-3.072l186.88-102.4a29.952 29.952 0 0 1 41.984 12.288c14.848 14.848 6.144 36.352-9.216 45.056l-186.88 102.4a66.56 66.56 0 0 1-54.272 6.144 92.16 92.16 0 0 1-48.128-36.352l-102.4-186.88a32.5632 32.5632 0 0 1 6.144-36.352z M351.488 736.768h-4.096c-16.896-4.096-29.696-21.504-25.6-38.4C372.992 395.264 641.792 118.272 957.44 36.864c16.896-4.096 34.304 4.096 38.4 21.504s-4.096 34.304-21.504 38.4C684.032 173.568 432.128 433.664 380.928 706.56a29.184 29.184 0 0 1-29.696 30.208z',
        viewBox: '0 0 1024 1024',
    },
    'detail': {
        path: 'M756.315353 350.539419h-467.385892c-23.369295 0-42.489627-19.120332-42.489627-42.489626s19.120332-42.489627 42.489627-42.489627h467.385892c23.369295 0 42.489627 19.120332 42.489626 42.489627s-19.120332 42.489627-42.489626 42.489626zM756.315353 786.058091h-467.385892c-23.369295 0-42.489627-19.120332-42.489627-42.489626s19.120332-42.489627 42.489627-42.489627h467.385892c23.369295 0 42.489627 19.120332 42.489626 42.489627s-19.120332 42.489627-42.489626 42.489626z M851.917012 1024H172.082988c-72.232365 0-129.593361-57.360996-129.593361-129.593361v-764.813278C42.489627 57.360996 99.850622 0 172.082988 0h679.834024c72.232365 0 129.593361 57.360996 129.593361 129.593361v764.813278c0 72.232365-57.360996 129.593361-129.593361 129.593361zM172.082988 46.738589C125.344398 46.738589 89.228216 84.979253 89.228216 129.593361v764.813278C89.228216 939.020747 125.344398 977.261411 172.082988 977.261411h679.834024c46.738589 0 82.854772-38.240664 82.854772-82.854772v-764.813278c0-46.738589-38.240664-82.854772-82.854772-82.854772H172.082988z M745.692946 569.360996H278.307054c-23.369295 0-42.489627-19.120332-42.489627-42.489627s19.120332-42.489627 42.489627-42.489626h467.385892c23.369295 0 42.489627 19.120332 42.489627 42.489626s-19.120332 42.489627-42.489627 42.489627z',
        viewBox: '0 0 1024 1024',
    },
    'default-pic': {
        path: 'M335.36 440.32c38.4 0 71.68-30.72 71.68-71.68 0-38.4-30.72-71.68-71.68-71.68-38.4 0-71.68 30.72-71.68 71.68 2.56 40.96 33.28 71.68 71.68 71.68z m0 0 M652.8 396.8L445.44 691.2l-107.52-151.04-179.2 253.44h704l-209.92-396.8z m0 0 M934.4 107.52h-844.8c-20.48 0-35.84 15.36-35.84 35.84v739.84c0 20.48 15.36 35.84 35.84 35.84h844.8c20.48 0 35.84-15.36 35.84-35.84V143.36c0-20.48-15.36-35.84-35.84-35.84z m-35.84 739.84H125.44V176.64h775.68v670.72h-2.56z m0 0',
        viewBox: '0 0 1024 1024',
    },
};

const SvgIcon: React.FC<SvgIconProps> = ({
    size = '1em',
    color,
    className = '',
    name,
    svgUrl,
    style,
}) => {
    const svgRef = useRef<HTMLDivElement>(null);
    const [loadingError, setLoadingError] = useState(false);

    // 优先使用style中的color，然后是color prop，最后是currentColor（继承自className）
    const actualColor = style?.color || color || 'currentColor';

    // 优先使用style中的width和height，然后是size参数
    const actualWidth = style?.width || size;
    const actualHeight = style?.height || size;

    // 加载外部SVG并设置颜色/大小
    const loadExternalSvg = useCallback(async () => {
        if (!svgUrl || !svgRef.current) return;

        try {
            // 优先使用缓存
            let svgText = svgCache[svgUrl];
            if (!svgText) {
                const response = await fetch(svgUrl);
                if (!response.ok) throw new Error('SVG加载失败');
                svgText = await response.text();
                svgCache[svgUrl] = svgText; // 存入缓存
            }

            // 解析并修改SVG样式
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = svgText;
            const svgElement = tempDiv.querySelector('svg');

            if (svgElement) {
                // 设置SVG基础属性
                svgElement.setAttribute('width', String(actualWidth));
                svgElement.setAttribute('height', String(actualHeight));
                svgElement.setAttribute('class', className);

                // 给所有可填充元素设置颜色（排除fill="none"的元素）
                const fillElements = svgElement.querySelectorAll(
                    'path, circle, rect, polygon, ellipse',
                );
                fillElements.forEach(el => {
                    if (el.getAttribute('fill') !== 'none') {
                        el.setAttribute('fill', actualColor);
                    }
                });

                // 渲染到页面
                svgRef.current.innerHTML = '';
                svgRef.current.appendChild(svgElement);
            }
        } catch (err) {
            console.error('加载SVG失败:', err);
            setLoadingError(true);
        }
    }, [svgUrl, actualWidth, actualHeight, actualColor, className]);

    // 加载外部SVG
    useEffect(() => {
        if (svgUrl) {
            loadExternalSvg();
        }
    }, [svgUrl, loadExternalSvg]);

    // 1. 外部SVG加载逻辑
    if (svgUrl) {
        return (
            <div
                ref={svgRef}
                style={{
                    width: actualWidth,
                    height: actualHeight,
                    display: 'inline-block',
                }}
                className={className}
            >
                {loadingError && (
                    <span style={{ color: 'red' }}>SVG加载失败</span>
                )}
            </div>
        );
    }

    // 2. 预定义图标渲染逻辑
    const iconData = name ? iconPaths[name] : null;
    const path = iconData?.path || '';
    const viewBox = iconData?.viewBox || '0 0 1024 1024';

    return (
        <svg
            className={className}
            viewBox={viewBox}
            width={actualWidth}
            height={actualHeight}
            style={style}
        >
            <path d={path} fill={actualColor} />
        </svg>
    );
};

export default SvgIcon;
